import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import { Spin } from 'antd';
import { createBrowserRouter, Navigate } from 'react-router-dom';

const LoginPage = lazy(() =>
  import('./views/LoginPage.js').then((module) => ({ default: module.LoginPage })),
);
const WorkspaceLayout = lazy(() =>
  import('./views/WorkspaceLayout.js').then((module) => ({ default: module.WorkspaceLayout })),
);
const RootRedirect = lazy(() =>
  import('./views/WorkspaceLayout.js').then((module) => ({ default: module.RootRedirect })),
);
const LeadListPage = lazy(() =>
  import('./views/leads.js').then((module) => ({ default: module.LeadListPage })),
);
const LeadDetailPage = lazy(() =>
  import('./views/leads.js').then((module) => ({ default: module.LeadDetailPage })),
);
const SendingTaskPage = lazy(() =>
  import('./views/sending-tasks.js').then((module) => ({ default: module.SendingTaskPage })),
);
const ManualOutreachPage = lazy(() =>
  import('./views/manual-outreach.js').then((module) => ({ default: module.ManualOutreachPage })),
);
const OverviewPage = lazy(() =>
  import('./views/workbench.js').then((module) => ({ default: module.OverviewPage })),
);
const ResearchPage = lazy(() =>
  import('./views/workbench.js').then((module) => ({ default: module.ResearchPage })),
);
const ContentPage = lazy(() =>
  import('./views/workbench.js').then((module) => ({ default: module.ContentPage })),
);
const RepliesPage = lazy(() =>
  import('./views/replies.js').then((module) => ({ default: module.RepliesPage })),
);
const ImportsPage = lazy(() =>
  import('./views/import-center.js').then((module) => ({ default: module.ImportCenterPage })),
);
const ExceptionsPage = lazy(() =>
  import('./views/workbench.js').then((module) => ({ default: module.ExceptionsPage })),
);
const CrmSyncPage = lazy(() =>
  import('./views/workbench.js').then((module) => ({ default: module.CrmSyncPage })),
);
const CollectorPage = lazy(() =>
  import('./views/collector.js').then((module) => ({ default: module.CollectorPage })),
);
const MessagingSettingsPage = lazy(() =>
  import('./views/messaging-settings.js').then((module) => ({
    default: module.MessagingSettingsPage,
  })),
);
const SuppressionsPage = lazy(() =>
  import('./views/suppressions.js').then((module) => ({ default: module.SuppressionsPage })),
);
const FormProfilesPage = lazy(() =>
  import('./views/form-profiles.js').then((module) => ({ default: module.FormProfilesPage })),
);
const AiSettingsPage = lazy(() =>
  import('./views/ai-settings.js').then((module) => ({ default: module.AiSettingsPage })),
);
const RapidApiSettingsPage = lazy(() =>
  import('./views/rapidapi-settings.js').then((module) => ({
    default: module.RapidApiSettingsPage,
  })),
);
const TenancySettingsPage = lazy(() =>
  import('./views/tenancy-settings.js').then((module) => ({
    default: module.TenancySettingsPage,
  })),
);
const BusinessContentPage = lazy(() =>
  import('./views/business-content.js').then((module) => ({ default: module.BusinessContentPage })),
);
const OperationsPage = lazy(() =>
  import('./views/operations.js').then((module) => ({ default: module.OperationsPage })),
);
const AnalyticsPage = lazy(() =>
  import('./views/analytics.js').then((module) => ({ default: module.AnalyticsPage })),
);

const pending = (element: ReactNode) => (
  <Suspense
    fallback={
      <div className="route-loading">
        <Spin size="large" />
      </div>
    }
  >
    {element}
  </Suspense>
);

export const router = createBrowserRouter([
  { path: '/login', element: pending(<LoginPage />) },
  {
    path: '/',
    element: pending(<WorkspaceLayout />),
    children: [
      { index: true, element: pending(<RootRedirect />) },
      { path: 'app/:businessId/overview', element: pending(<OverviewPage />) },
      { path: 'app/:businessId/leads', element: pending(<LeadListPage />) },
      { path: 'app/:businessId/leads/:leadId', element: pending(<LeadDetailPage />) },
      { path: 'app/:businessId/research', element: pending(<ResearchPage />) },
      { path: 'app/:businessId/content', element: pending(<ContentPage />) },
      { path: 'app/:businessId/sending', element: pending(<SendingTaskPage />) },
      { path: 'app/:businessId/sending/manual', element: pending(<ManualOutreachPage />) },
      { path: 'app/:businessId/replies', element: pending(<RepliesPage />) },
      { path: 'app/:businessId/acquisition/imports', element: pending(<ImportsPage />) },
      { path: 'app/:businessId/acquisition/collector', element: pending(<CollectorPage />) },
      { path: 'app/:businessId/exceptions', element: pending(<ExceptionsPage />) },
      { path: 'app/:businessId/crm-sync', element: pending(<CrmSyncPage />) },
      { path: 'app/:businessId/settings/channels', element: pending(<MessagingSettingsPage />) },
      { path: 'app/:businessId/settings/suppressions', element: pending(<SuppressionsPage />) },
      { path: 'app/:businessId/settings/form-profiles', element: pending(<FormProfilesPage />) },
      { path: 'app/:businessId/settings/ai', element: pending(<AiSettingsPage />) },
      {
        path: 'organization/:organizationId/settings/rapidapi',
        element: pending(<RapidApiSettingsPage />),
      },
      { path: 'app/:businessId/settings/access', element: pending(<TenancySettingsPage />) },
      { path: 'app/:businessId/settings/business', element: pending(<BusinessContentPage />) },
      { path: 'app/:businessId/operations', element: pending(<OperationsPage />) },
      { path: 'app/:businessId/analytics', element: pending(<AnalyticsPage />) },
      { path: 'businesses/:businessId/leads', element: pending(<LeadListPage />) },
      { path: 'businesses/:businessId/leads/:leadId', element: pending(<LeadDetailPage />) },
      { path: 'businesses/:businessId/sending-tasks', element: pending(<SendingTaskPage />) },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
